import { createError, H3Event } from 'h3';
import { SessionService } from './session.service';
import { AuthSessionData } from '../types/auth-session.types';
import type { AnalogAuthConfig } from '../types/auth.types';
import { inject, registerService, Injectable } from '@analog-tools/inject';
import { LoggerService } from '@analog-tools/logger';
import { getSession, refetchSession, regenerateSession, updateSession } from '@analog-tools/session';
import { createRemoteJWKSet, jwtVerify, type JWTPayload } from 'jose';
import { base64url } from '../utils/pkce';

const AUTH_FAILED_MESSAGE = 'Authentication failed';

// http is only acceptable for these hosts (local development).
function isLocalDevHost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '::1' ||
    hostname === '[::1]' ||
    /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)
  );
}

/**
 * Parse a URL and require https, except for local-development hosts. Guards
 * against tokens/secrets being sent over cleartext or to a non-https endpoint
 * advertised by a tampered discovery document.
 */
function assertSecureUrl(
  rawUrl: string,
  label: string,
  allowInsecureLocalhost: boolean
): URL {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    throw createError({ statusCode: 500, message: `Invalid ${label} URL` });
  }
  const isSecure =
    url.protocol === 'https:' ||
    (allowInsecureLocalhost &&
      url.protocol === 'http:' &&
      isLocalDevHost(url.hostname));
  if (!isSecure) {
    throw createError({
      statusCode: 500,
      message: `${label} must use https (http is only permitted for a localhost issuer during development)`,
    });
  }
  return url;
}

function normalizeIssuer(issuer: string): string {
  return issuer.replace(/\/+$/, '');
}

/**
 * Build the set of `iss` claim values (trailing-slash variants) accepted for
 * a single configured issuer. Providers disagree on the canonical form:
 * Auth0 emits `https://tenant.auth0.com/` (trailing slash) in both the
 * discovery document and the id_token `iss`, while Keycloak and most others
 * emit it without the slash. The variants stay anchored to the one configured
 * issuer, so they never broaden trust to a different host.
 */
function issuerVariants(issuer: string): string[] {
  const normalized = normalizeIssuer(issuer);
  return [...new Set([issuer, normalized, `${normalized}/`])];
}
// OIDC Core §3.1.3.6: at_hash is half of the access token hashed with the
// digest matching the ID token's signing algorithm's bit size (EdDSA uses
// SHA-512 per the OIDC errata). Unknown algs return null so the caller can
// fail closed instead of skipping a claim that's actually present.
function hashAlgForJwsAlg(alg: string): 'SHA-256' | 'SHA-384' | 'SHA-512' | null {
  if (alg === 'EdDSA') return 'SHA-512';
  const bits = /^[A-Z]{2}(256|384|512)$/.exec(alg)?.[1];
  return bits ? (`SHA-${bits}` as 'SHA-256' | 'SHA-384' | 'SHA-512') : null;
}

async function computeAtHash(accessToken: string, alg: string): Promise<string | null> {
  const hashAlg = hashAlgForJwsAlg(alg);
  if (!hashAlg) return null;
  const digest = await globalThis.crypto.subtle.digest(
    hashAlg,
    new TextEncoder().encode(accessToken)
  );
  const bytes = new Uint8Array(digest);
  return base64url(bytes.slice(0, bytes.length / 2));
}

/**
 * Service for handling OAuth authentication in a Backend-for-Frontend pattern
 */

@Injectable()
export class OAuthAuthenticationService {
  private logger: LoggerService;
  private inflightRefreshes = new Map<
    string,
    Promise<{
      access_token: string;
      id_token?: string;
      refresh_token?: string;
      expires_in: number;
    }>
  >();

  constructor(config: AnalogAuthConfig) {
    this.logger = inject(LoggerService).forContext(
      'OAuthAuthenticationService'
    );
    registerService(SessionService, config.sessionStorage);
    this.config = config;
  }

  // Config object with default values
  private readonly config!: AnalogAuthConfig;

  // OpenID Configuration cache
  private openIDConfigCache: OpenIDConfiguration | null = null;
  private configLastFetched: number | null = null;
  private readonly CONFIG_CACHE_TTL = 3600000; // 1 hour in milliseconds

  // Add these properties for token refresh configuration
  private TOKEN_REFRESH_SAFETY_MARGIN = 60 * 5; // 5 minutes in seconds

  // Cached normalized whitelist extensions for efficient lookups
  private normalizedWhitelistExtensions: Set<string> = new Set();

  // Cached remote JWKS for ID-token verification (keyed by jwks_uri)
  private jwks?: ReturnType<typeof createRemoteJWKSet>;
  private jwksUri?: string;

  /**
   * Validate that the service has been properly initialized
   * @throws Error if mandatory configuration is missing
   */
  private validateConfiguration(): void {
    if (
      !this.config.issuer ||
      !this.config.clientId ||
      !this.config.clientSecret ||
      !this.config.callbackUri
    ) {
      throw new Error(
        'OAuth Authentication Service not properly initialized. ' +
          'Make sure to call AnalogAuth() with valid configuration before using authentication features.'
      );
    }
  }

  // Rest of the code converted to class methods...

  /**
   * Initialize session for the request
   */
  async initSession(event: H3Event): Promise<void> {
    return await inject(SessionService).initSession(event);
  }

  getConfig(): AnalogAuthConfig {
    this.validateConfiguration();
    return this.config;
  }

  /**
   * Safely access a configuration value
   * @param key The configuration key to retrieve
   * @param fallbackValue Optional fallback value if the config value doesn't exist
   * @returns The configuration value or fallback value
   * @throws Error if the configuration value doesn't exist and no fallback is provided
   */
  getConfigValue<K extends keyof AnalogAuthConfig>(
    key: K,
    fallbackValue?: AnalogAuthConfig[K]
  ): AnalogAuthConfig[K] {
    const value = this.config[key];
    // Check if value is undefined or empty string
    if (value === undefined || (typeof value === 'string' && value === '')) {
      if (fallbackValue !== undefined) {
        return fallbackValue;
      }

      // These config values are optional and should return a safe default if missing
      if (key === 'userHandler' || key === 'logoutUrl' || key === 'audience') {
        return undefined as AnalogAuthConfig[K];
      }
      if (key === 'unprotectedRoutes') {
        return [] as string[] as AnalogAuthConfig[K];
      }

      throw new Error(`Configuration value for '${key}' doesn't exist`);
    }
    return value;
  }

  /**
   * Check if the route is unprotected
   * @param path The request path
   * @returns True if the route is unprotected, false otherwise
   */
  isUnprotectedRoute(path: string): boolean {
    const unprotectedRoutes = this.getConfigValue(
      'unprotectedRoutes',
      [] as string[]
    );
    
    if (!Array.isArray(unprotectedRoutes)) {
      return false;
    }
    
    return unprotectedRoutes.some((route) => {
      // Handle wildcard routes (ending with *)
      if (route.endsWith('*')) {
        const routePrefix = route.slice(0, -1);
        
        // For wildcards, we want to match paths that have actual content after the prefix
        // Examples:
        // - `/api/public/*` should NOT match `/api/public` or `/api/public/`
        // - `/api/public/*` should match `/api/public/subpath`, `/api/public/anything`
        if (!path.startsWith(routePrefix)) {
          return false;
        }
        
        // Check if there's actual content after the prefix (not just empty or single slash)
        const afterPrefix = path.slice(routePrefix.length);
        return afterPrefix.length > 0 && afterPrefix !== '/';
      }
      
      // Handle exact route matching - normalize trailing slashes
      const normalizedRoute = route.endsWith('/') ? route : route + '/';
      const normalizedPath = path.endsWith('/') ? path : path + '/';
      
      // Check both with and without trailing slash
      return path === route || normalizedPath === normalizedRoute;
    });
  }

  /**
   * Get OAuth authorization URL for login
   */
  async getAuthorizationUrl(params: {
    state: string;
    codeChallenge: string;
    nonce: string;
    redirectUri?: string;
  }): Promise<string> {
    this.validateConfiguration();

    const config = await this.getOpenIDConfiguration();

    const audience = this.getConfigValue('audience', undefined);

    const searchparams = {
      response_type: 'code',
      client_id: this.getConfigValue('clientId'),
      redirect_uri: params.redirectUri || this.getConfigValue('callbackUri'),
      scope: this.getConfigValue('scope'),
      state: params.state,
      nonce: params.nonce,
      code_challenge: params.codeChallenge,
      code_challenge_method: 'S256',
      ...(audience ? { audience } : {}),
    };

    const urlParams = new URLSearchParams(searchparams);

    return `${config.authorization_endpoint}?${urlParams.toString()}`;
  }

  /**
   * Exchange authorization code for tokens
   */
  private async exchangeCodeForTokens(
    code: string,
    codeVerifier: string,
    redirectUri?: string
  ) {
    const config = await this.getOpenIDConfiguration();

    const response = await fetch(config.token_endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: this.getConfigValue('clientId'),
        client_secret: this.getConfigValue('clientSecret'),
        code,
        code_verifier: codeVerifier,
        redirect_uri: redirectUri || this.getConfigValue('callbackUri'),
      }).toString(),
    });
    if (!response.ok) {
      const error = await response.json();
      this.logger.error('Error exchanging code for tokens', error);
      throw createError({
        statusCode: 401,
        message: 'Failed to exchange authorization code',
      });
    }

    return await response.json();
  }

  /**
   * Refresh access token using refresh token
   */
  private async refreshTokens(refreshToken: string): Promise<{
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
  }> {
    const config = await this.getOpenIDConfiguration();

    try {
      const response = await fetch(config.token_endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.getConfigValue('clientId'),
          client_secret: this.getConfigValue('clientSecret'),
          refresh_token: refreshToken,
        }).toString(),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ error: 'Unknown error' }));
        this.logger.error('Error refreshing token', error);
        throw createError({
          statusCode: 401,
          message: 'Failed to refresh token',
        });
      }

      return await response.json();
    } catch (error) {
      this.logger.error('Error during token refresh', error);
      throw createError({
        statusCode: 401,
        message: 'Failed to refresh authentication token',
      });
    }
  }

  private async refreshTokensDeduped(refreshToken: string): Promise<{
    access_token: string;
    id_token?: string;
    refresh_token?: string;
    expires_in: number;
  }> {
    const existingRefresh = this.inflightRefreshes.get(refreshToken);
    if (existingRefresh) {
      return existingRefresh;
    }

    const refreshPromise = this.refreshTokens(refreshToken).finally(() => {
      this.inflightRefreshes.delete(refreshToken);
    });

    this.inflightRefreshes.set(refreshToken, refreshPromise);
    return refreshPromise;
  }

  /**
   * Get user info from OAuth provider with improved error handling and retry logic
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async getUserInfo(accessToken: string, maxRetries = 3): Promise<any> {
    const config = await this.getOpenIDConfiguration();
    let lastError: Error | null = null;

    // Retry logic for network issues
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(config.userinfo_endpoint, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          // Add timeout to prevent hanging requests
          signal: AbortSignal.timeout(10000), // 10 second timeout
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Unknown error' }));
          this.logger.error(`Error getting user info`, errorData, {
            attempt,
            maxRetries,
          });

          // Handle different error scenarios
          if (response.status === 401) {
            throw createError({
              statusCode: 401,
              message: 'Authentication token is invalid or expired',
            });
          } else if (response.status === 429) {
            // Rate limiting - wait longer before retry
            const retryAfter = parseInt(
              response.headers.get('Retry-After') || '5',
              10
            );
            await new Promise((resolve) =>
              setTimeout(resolve, retryAfter * 1000)
            );
            continue;
          } else if (response.status >= 500) {
            // Server error - retry after delay
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            continue;
          } else {
            throw createError({
              statusCode: response.status,
              message: `Failed to get user info: ${
                errorData.error || 'Unknown error'
              }`,
            });
          }
        }

        const userData = await response.json();

        // Basic validation of user data
        if (!userData || (!userData.sub && !userData.id)) {
          throw createError({
            statusCode: 500,
            message: 'Invalid user data received from provider',
          });
        }

        return userData;
      } catch (error: unknown) {
        lastError = error as Error;

        // Don't retry certain errors like invalid token
        if (
          error instanceof Error &&
          'statusCode' in error &&
          (error as { statusCode: number }).statusCode === 401
        ) {
          throw error;
        }

        // Network errors are retryable
        if (
          error instanceof TypeError ||
          (error instanceof Error && error.name === 'AbortError')
        ) {
          this.logger.error(`Network error fetching user info`, error, {
            attempt,
            maxRetries,
          });
          if (attempt < maxRetries) {
            // Exponential backoff
            await new Promise((resolve) =>
              setTimeout(resolve, Math.pow(2, attempt) * 500)
            );
            continue;
          }
        }

        // If we're out of retries, rethrow the last error
        if (attempt === maxRetries) {
          this.logger.error(`Failed to get user info after multiple attempts`, {
            maxRetries,
          });
          throw createError({
            statusCode: 500,
            message: 'Failed to get user info after multiple attempts',
            cause: lastError,
          });
        }
      }
    }

    // This should never be reached due to the throw in the last attempt
    throw createError({
      statusCode: 500,
      message: 'Unexpected error getting user info',
    });
  }

  /**
   * Verify an OIDC ID token: signature (via the provider's JWKS), `iss`, `aud`,
   * `exp`/`nbf`, the per-login `nonce`, `azp` (when present), and — when the
   * provider sends it — `at_hash` binding the token to `accessToken`. Returns
   * the verified claims
   * so the caller can anchor identity in the cryptographically validated token.
   */
  private async validateIdToken(
    idToken: string,
    nonce: string,
    accessToken: string
  ): Promise<JWTPayload> {
    const config = await this.getOpenIDConfiguration();

    if (!config.jwks_uri) {
      throw createError({
        statusCode: 500,
        message: 'OpenID configuration is missing jwks_uri; cannot verify ID token',
      });
    }

    // Only a localhost issuer (development) may use cleartext http; a production
    // (https) issuer must never fetch signing keys over http, even for localhost.
    const configuredIssuer = this.getConfigValue('issuer');
    const issuerUrl = assertSecureUrl(configuredIssuer, 'OAuth issuer', true);
    const allowInsecureLocalhost =
      issuerUrl.protocol === 'http:' && isLocalDevHost(issuerUrl.hostname);

    const jwksUrl = assertSecureUrl(
      config.jwks_uri,
      'jwks_uri',
      allowInsecureLocalhost
    );

    if (!this.jwks || this.jwksUri !== config.jwks_uri) {
      this.jwks = createRemoteJWKSet(jwksUrl);
      this.jwksUri = config.jwks_uri;
    }

    let payload: JWTPayload;
    let protectedHeader: { alg: string };
    try {
      ({ payload, protectedHeader } = await jwtVerify(idToken, this.jwks, {
        // Trust anchor is the configured issuer, never the fetched metadata.
        // Accept every trailing-slash variant of the configured issuer so an
        // id_token `iss` from both slash-preferring providers (Auth0) and
        // slash-stripping providers (Keycloak) verify against the same config.
        issuer: issuerVariants(configuredIssuer),
        audience: this.getConfigValue('clientId'),
      }));
    } catch (error) {
      this.logger.error('ID token validation failed', error);
      throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
    }

    if (payload['nonce'] !== nonce) {
      this.logger.error('ID token nonce mismatch');
      throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
    }

    if (
      typeof payload['azp'] !== 'undefined' &&
      payload['azp'] !== this.getConfigValue('clientId')
    ) {
      this.logger.error('ID token azp does not match client ID');
      throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
    }

    // Bind the ID token to the access token it was issued alongside, when the
    // provider sends at_hash (OIDC Core §3.1.3.6). Absent = provider doesn't
    // send it; present-but-wrong = token substitution, fail closed either way.
    if (typeof payload['at_hash'] === 'string') {
      const expected = await computeAtHash(accessToken, protectedHeader.alg);
      if (expected === null || expected !== payload['at_hash']) {
        this.logger.error('ID token at_hash mismatch');
        throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
      }
    }

    return payload;
  }

  /**
   * Handle OAuth callback
   */
  async handleCallback(event: H3Event, code: string, state: string) {
    // Verify state parameter (should be implemented with CSRF protection)
    if (!state) {
      throw createError({
        statusCode: 400,
        message: 'Invalid state parameter',
      });
    }

    // PKCE verifier and nonce were stored on the session by the login route.
    const session = getSession<AuthSessionData>(event);
    const codeVerifier = session?.codeVerifier;
    const nonce = session?.nonce;
    if (!codeVerifier || !nonce) {
      throw createError({
        statusCode: 400,
        message: 'Missing PKCE verifier or nonce; restart the login flow',
      });
    }

    // Exchange code for tokens (PKCE)
    const tokens = await this.exchangeCodeForTokens(code, codeVerifier);
    const { access_token, id_token, refresh_token, expires_in } = tokens;

    const requestsOpenIdScope = this.getConfigValue('scope')
      .split(' ')
      .includes('openid');
    if (requestsOpenIdScope && !id_token) {
      this.logger.error(
        'Token response is missing id_token for an openid-scoped request'
      );
      throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
    }

    // Verify the ID token (authenticity + nonce replay protection) when present.
    let idTokenSub: string | undefined;
    if (id_token) {
      const claims = await this.validateIdToken(id_token, nonce, access_token);
      if (typeof claims.sub !== 'string' || claims.sub.length === 0) {
        this.logger.error('ID token is missing a subject');
        throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
      }
      idTokenSub = claims.sub;
    }

    // Get user info from OAuth provider (profile enrichment)
    const userData = await this.getUserInfo(access_token);

    // Anchor identity in the verified ID token: userinfo must describe the same
    // subject, otherwise the tokens have been mixed/substituted.
    if (
      idTokenSub &&
      (typeof userData?.sub !== 'string' || userData.sub !== idTokenSub)
    ) {
      this.logger.error('ID token subject does not match userinfo subject');
      throw createError({ statusCode: 401, message: AUTH_FAILED_MESSAGE });
    }

    // Store user in database
    const userHandler = this.getConfigValue('userHandler', undefined);

    let user = userData;
    if (userHandler && 'createOrUpdateUser' in userHandler) {
      user = await userHandler.createOrUpdateUser?.(userData);
    }

    // Store auth data in session using new session API
    const auth = {
      isAuthenticated: true,
      accessToken: access_token,
      idToken: id_token,
      refreshToken: refresh_token,
      expiresAt: Date.now() + expires_in * 1000,
      userInfo: userData,
    };

    // Regenerate the session id on privilege elevation (anonymous ->
    // authenticated) so any pre-auth id a fixation attacker may have planted is
    // discarded before the authenticated tokens are stored under a fresh id.
    await regenerateSession(event);

    // Update session with user and auth data; clear transient PKCE/nonce.
    await updateSession(event, () => ({
      user,
      auth,
      codeVerifier: undefined,
      nonce: undefined,
    }));

    if (this.getConfigValue('singleSessionPerUser', false)) {
      // Optional policy: keep current request session and invalidate
      // other authenticated sessions for the same resolved user identity.
      // Guarded so a storage error in this optional cleanup can't fail an
      // otherwise-successful login (tokens are already saved above).
      try {
        await this.invalidateOtherUserSessions(event, user, userData);
      } catch (error) {
        this.logger.error('Failed to invalidate other user sessions', error);
      }
    }

    // Log successful session save
    this.logger.debug('Authentication session data saved successfully');

    return { user, tokens };
  }

  private resolveUserIdentity(
    user: Record<string, unknown> | null | undefined,
    userData: Record<string, unknown> | null | undefined
  ): string | null {
    const candidates = [
      user?.['id'],
      user?.['sub'],
      userData?.['id'],
      userData?.['sub'],
      userData?.['email'],
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.length > 0) {
        return candidate;
      }
    }

    return null;
  }

  private resolveSessionIdentity(session: AuthSessionData): string | null {
    const sessionUser =
      session.user && typeof session.user === 'object'
        ? (session.user as Record<string, unknown>)
        : null;
    const sessionUserInfo =
      session.auth?.userInfo && typeof session.auth.userInfo === 'object'
        ? (session.auth.userInfo as Record<string, unknown>)
        : null;

    const candidates = [
      sessionUser?.['id'],
      sessionUser?.['sub'],
      sessionUserInfo?.['id'],
      sessionUserInfo?.['sub'],
      sessionUserInfo?.['email'],
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.length > 0) {
        return candidate;
      }
    }

    return null;
  }

  private async invalidateOtherUserSessions(
    event: H3Event,
    user: Record<string, unknown> | null | undefined,
    userData: Record<string, unknown> | null | undefined
  ): Promise<void> {
    const targetIdentity = this.resolveUserIdentity(user, userData);
    if (!targetIdentity) {
      this.logger.debug(
        'Skipping session invalidation because user identity could not be resolved'
      );
      return;
    }

    const currentSessionId =
      typeof event.context['__session_id__'] === 'string'
        ? (event.context['__session_id__'] as string)
        : null;

    if (!currentSessionId) {
      this.logger.debug(
        'Skipping session invalidation because current session id is unavailable'
      );
      return;
    }

    const sessionService = inject(SessionService);
    const activeSessions = await sessionService.getActiveSessions();

    for (const activeSession of activeSessions) {
      if (activeSession.id === currentSessionId) {
        continue;
      }

      if (!activeSession.data.auth?.isAuthenticated) {
        continue;
      }

      const sessionIdentity = this.resolveSessionIdentity(activeSession.data);
      if (!sessionIdentity || sessionIdentity !== targetIdentity) {
        continue;
      }

      activeSession.update((data) => {
        const currentAuth = data.auth;
        return {
          ...data,
          auth: {
            ...(currentAuth ?? { isAuthenticated: false }),
            isAuthenticated: false,
            accessToken: undefined,
            idToken: undefined,
            refreshToken: undefined,
            expiresAt: undefined,
            userInfo: undefined,
          },
          user: null,
        };
      });

      await activeSession.save();
      this.logger.debug('Invalidated stale authenticated session', {
        sessionId: activeSession.id,
      });
    }
  }

  /**
   * Calculate if token needs refresh based on safety margin
   * @param expiresAt Timestamp when token expires
   * @returns True if token should be refreshed
   */
  private shouldRefreshToken(expiresAt: number): boolean {
    const now = Date.now();
    // Convert safety margin to milliseconds
    const safetyMargin = this.TOKEN_REFRESH_SAFETY_MARGIN * 1000;
    // Return true if token will expire within safety margin
    return now + safetyMargin > expiresAt;
  }

  /**
   * Serverless-compatible method to refresh expiring tokens
   * This should be called by a scheduled function/CRON job
   * rather than using setInterval which doesn't work reliably in serverless
   */
  async refreshExpiringTokens(): Promise<{
    refreshed: number;
    failed: number;
    total: number;
  }> {
    this.logger.debug('Starting bulk token refresh process');
    
    try {
      // Get all active sessions from the session service
      const sessionService = inject(SessionService);
      const activeSessions = await sessionService.getActiveSessions();
      
      let refreshed = 0;
      let failed = 0;
      const total = activeSessions.length;
      
      this.logger.debug(`Found ${total} active sessions to check`);
      
      // Process each session
      for (const session of activeSessions) {
        try {
          // Skip sessions without valid auth data
          if (!session.data.auth?.isAuthenticated || 
              !session.data.auth.refreshToken || 
              !session.data.auth.expiresAt) {
            this.logger.debug(`Skipping session ${session.id} - no valid auth data`);
            continue;
          }
          
          // Check if token needs refresh (within safety margin)
          if (!this.shouldRefreshToken(session.data.auth.expiresAt)) {
            this.logger.debug(`Skipping session ${session.id} - token not expiring soon`);
            continue;
          }
          
          this.logger.debug(`Refreshing token for session ${session.id}`);
          
          // Refresh the token (deduped so this doesn't race a concurrent
          // request-driven refresh for the same refresh token)
          const tokens = await this.refreshTokensDeduped(
            session.data.auth.refreshToken
          );
          
          // Update session data
          session.update((data) => {
            const currentAuth = data.auth;
            if (!currentAuth) return data;
            
            return {
              ...data,
              auth: {
                ...currentAuth,
                accessToken: tokens.access_token,
                idToken: tokens.id_token || currentAuth.idToken,
                refreshToken: tokens.refresh_token || currentAuth.refreshToken,
                expiresAt: Date.now() + tokens.expires_in * 1000,
              },
            };
          });
          
          // Save the updated session
          await session.save();
          
          refreshed++;
          this.logger.debug(`Successfully refreshed token for session ${session.id}`);
          
        } catch (error) {
          failed++;
          this.logger.error(
            'Failed to refresh token for session',
            error,
            { sessionId: session.id }
          );

          // Mark session as unauthenticated on refresh failure, unless a
          // concurrent request-driven refresh already updated this same
          // session (in storage) with a still-valid token in the meantime.
          try {
            const latestData = await session.refetch();
            const hasLatestValidAuth =
              !!latestData?.auth?.isAuthenticated &&
              typeof latestData.auth.expiresAt === 'number' &&
              latestData.auth.expiresAt > Date.now();

            if (hasLatestValidAuth) {
              failed--;
              this.logger.debug(
                `Skipping unauthenticated mark for session ${session.id} - already refreshed concurrently`
              );
              continue;
            }

            session.update((data) => {
              const currentAuth = data.auth;
              if (!currentAuth) return data;

              return {
                ...data,
                auth: {
                  ...currentAuth,
                  isAuthenticated: false,
                },
              };
            });
            await session.save();
          } catch (updateError) {
            this.logger.error(
              'Failed to update session after refresh failure',
              updateError,
              { sessionId: session.id }
            );
          }
        }
      }
      
      const result = { refreshed, failed, total };
      this.logger.info(`Bulk token refresh completed`, result);
      
      return result;
      
    } catch (error) {
      this.logger.error('Error during bulk token refresh', error);
      throw createError({
        statusCode: 500,
        message: 'Failed to refresh expiring tokens',
      });
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(event: H3Event): Promise<boolean> {
    await inject(SessionService).initSession(event);

    const session = (await getSession(event)) as AuthSessionData;

    // Ensure auth object exists in session data
    if (!session?.auth) {
      await updateSession(event, () => ({
        auth: { isAuthenticated: false },
      }));

      return false;
    }

    // Check if session has auth data
    if (!session.auth.isAuthenticated) {
      return false;
    }

    // Check if token is expired
    if (session.auth.expiresAt && session.auth.expiresAt < Date.now()) {
      // Token is expired, try to refresh if refresh token exists
      if (session.auth.refreshToken) {
        try {
          // Refresh the token
          const tokens = await this.refreshTokensDeduped(
            session.auth.refreshToken
          );

          // Update session with new tokens
          await updateSession(event, (currentSession: AuthSessionData) => ({
            auth: {
              ...currentSession.auth,
              isAuthenticated: true,
              accessToken: tokens.access_token,
              idToken: tokens.id_token || session?.auth?.idToken,
              refreshToken: tokens.refresh_token || session?.auth?.refreshToken,
              expiresAt: Date.now() + tokens.expires_in * 1000,
            },
          }));

          return true;
        } catch (error) {
          this.logger.error('Error refreshing token', error);

          // Re-read from storage, not the request-scoped context copy: a
          // concurrent request may have already refreshed this session
          // successfully, and getSession(event) can never observe that.
          const latestSession = await refetchSession<AuthSessionData>(event);
          const hasLatestValidAuth =
            !!latestSession?.auth?.isAuthenticated &&
            typeof latestSession.auth.expiresAt === 'number' &&
            latestSession.auth.expiresAt > Date.now();

          if (hasLatestValidAuth) {
            return true;
          }

          // Clear auth data on refresh token failure
          await updateSession(event, (currentSession: AuthSessionData) => ({
            auth: {
              ...currentSession.auth,
              isAuthenticated: false,
            },
          }));

          this.logger.info('error occurred while refreshing token');
          this.logger.groupEnd(
            'OAuthAuthenticationService.isAuthenticated ' + event.path
          );
          return false;
        }
      } else {
        // No refresh token available
        this.logger.info(' No refresh token available');
        this.logger.groupEnd(
          'OAuthAuthenticationService.isAuthenticated ' + event.path
        );
        return false;
      }
    }

    // Check if token needs proactive refresh
    if (
      session.auth.expiresAt &&
      this.shouldRefreshToken(session.auth.expiresAt)
    ) {
      // Refresh in background without blocking the request
      setTimeout(async () => {
        try {
          const currentSession = (await getSession(event)) as AuthSessionData;
          if (
            !currentSession?.auth?.isAuthenticated ||
            !currentSession.auth.refreshToken
          ) {
            return; // Session no longer valid
          }

          const tokens = await this.refreshTokensDeduped(
            currentSession.auth.refreshToken
          );

          // Update session with new tokens
          await updateSession(event, (session: AuthSessionData) => ({
            auth: {
              ...session.auth,
              isAuthenticated: true,
              accessToken: tokens.access_token,
              idToken: tokens.id_token || currentSession?.auth?.idToken,
              refreshToken:
                tokens.refresh_token || currentSession?.auth?.refreshToken,
              expiresAt: Date.now() + tokens.expires_in * 1000,
            },
          }));

          this.logger.debug('Background token refresh completed');
        } catch (error) {
          this.logger.error('Background token refresh failed', error);
        }
      }, 0);
    }

    return true;
  }

  /**
   * Get authenticated user
   */
  async getAuthenticatedUser(event: H3Event) {
    // Check if user is authenticated
    if (!(await this.isAuthenticated(event))) {
      return null;
    }

    const session = getSession(event) as AuthSessionData;

    const userHandler = this.getConfigValue('userHandler', undefined);

    if (userHandler && 'mapUserToLocal' in userHandler) {
      return userHandler.mapUserToLocal?.(session.auth?.userInfo);
    }

    return session.auth?.userInfo;
  }

  /**
   * Revoke an access token
   */
  private async revokeToken(token: string): Promise<void> {
    const config = await this.getOpenIDConfiguration();
    const response = await fetch(config.revocation_endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: this.getConfigValue('clientId'),
        client_secret: this.getConfigValue('clientSecret'),
        token,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to revoke token');
    }
  }

  /**
   * Logout user
   */
  async logout(event: H3Event): Promise<string> {
    await inject(SessionService).initSession(event);
    const session = getSession(event) as AuthSessionData;
    const config = await this.getOpenIDConfiguration();

    // Revoke access token if it exists
    if (session?.['auth']?.accessToken) {
      try {
        await this.revokeToken(session['auth'].accessToken);
      } catch (error) {
        this.logger.error('Failed to revoke access token', error);
      }
    }

    // Revoke refresh token if it exists
    if (session?.auth?.refreshToken) {
      try {
        await this.revokeToken(session.auth.refreshToken);
      } catch (error) {
        this.logger.error('Failed to revoke refresh token', error);
      }
    }

    // Get OAuth logout URL
    const logoutUrl = new URL(config.end_session_endpoint);
    logoutUrl.searchParams.append('client_id', this.getConfigValue('clientId'));

    // Add returnTo parameter if configured
    const returnTo = this.getConfigValue('logoutUrl');

    if (returnTo) {
      logoutUrl.searchParams.append('returnTo', returnTo);
    }

    // Clear session
    await updateSession(event, () => ({
      auth: { isAuthenticated: false },
      user: null,
    }));

    return logoutUrl.toString();
  }

  /**
   * Fetch OpenID Configuration from the well-known endpoint
   */
  private async getOpenIDConfiguration(): Promise<OpenIDConfiguration> {
    const now = Date.now();

    // Return cached config if it's still valid
    if (
      this.openIDConfigCache &&
      this.configLastFetched &&
      now - this.configLastFetched < this.CONFIG_CACHE_TTL
    ) {
      return this.openIDConfigCache;
    }

    const issuer = this.getConfigValue('issuer');
    // Enforce https on the issuer itself: over TLS the discovery document and
    // every subsequent token/userinfo call are authenticated, so a network
    // attacker cannot forge the endpoints the client trusts. Only a localhost
    // dev issuer may use http; a production https issuer then forces https on
    // every advertised endpoint too.
    const issuerUrl = assertSecureUrl(issuer, 'OAuth issuer', true);
    const allowInsecureLocalhost =
      issuerUrl.protocol === 'http:' && isLocalDevHost(issuerUrl.hostname);

    let config: OpenIDConfiguration;
    try {
      const response = await fetch(
        `${normalizeIssuer(issuer)}/.well-known/openid-configuration`,
        {
          // Reject redirects: a network attacker could otherwise redirect
          // discovery to an attacker-controlled https endpoint that still
          // passes validation.
          redirect: 'error',
          signal: AbortSignal.timeout(
            this.getConfigValue('discoveryTimeoutMs', 10000) as number
          ),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch OpenID configuration: ${response.statusText}`
        );
      }

      config = await response.json();
    } catch (error) {
      this.logger.error('Error fetching OpenID configuration', error);
      throw createError({
        statusCode: 500,
        message: 'Failed to fetch OpenID configuration',
      });
    }

    this.assertTrustedOpenIDConfiguration(
      config,
      issuer,
      allowInsecureLocalhost
    );

    this.openIDConfigCache = config;
    this.configLastFetched = now;

    return config;
  }

  /**
   * Validate a fetched OpenID configuration before trusting its endpoints:
   * the document's `issuer` must match the configured issuer (OIDC Discovery
   * §4.3), and every advertised endpoint we use must be https (localhost aside).
   */
  private assertTrustedOpenIDConfiguration(
    config: OpenIDConfiguration,
    issuer: string,
    allowInsecureLocalhost: boolean
  ): void {
    if (
      typeof config.issuer !== 'string' ||
      normalizeIssuer(config.issuer) !== normalizeIssuer(issuer)
    ) {
      this.logger.error('OpenID configuration issuer mismatch', {
        expected: issuer,
        received: config.issuer,
      });
      throw createError({
        statusCode: 500,
        message:
          'OpenID configuration issuer does not match the configured issuer',
      });
    }

    const endpointKeys = [
      'authorization_endpoint',
      'token_endpoint',
      'userinfo_endpoint',
      'end_session_endpoint',
      'revocation_endpoint',
    ] as const;

    for (const key of endpointKeys) {
      const endpoint = config[key];
      if (typeof endpoint !== 'string' || endpoint.length === 0) {
        throw createError({
          statusCode: 500,
          message: `OpenID configuration is missing ${key}`,
        });
      }
      assertSecureUrl(endpoint, `OpenID ${key}`, allowInsecureLocalhost);
    }
  }
}

// OpenID Configuration interface
interface OpenIDConfiguration {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  end_session_endpoint: string;
  revocation_endpoint: string;
  jwks_uri?: string;
}
